import React, { useMemo } from "react";
import { Formik, Form } from "formik";
import styled, { css } from "styled-components";
import useHistory from "routing/useHistory";
import NumberUtils from "util/NumberUtils";
import { Col, Row } from "components/bootstrap";
import { FormikInput, FormSubmit, Section, Spinner } from "components/common";
import HideOnCloud from "util/conditional/HideOnCloud";
import HiddenFieldWrapper from "components/indices/IndexSetConfigurationForm/HiddenFieldWrapper";
import { parseFieldRestrictions } from "components/indices/helpers/fieldRestrictions";
import useArchiveIndexSetConfiguration from "archive/hooks/useArchiveIndexSetConfiguration";
import usePermissions from "hooks/usePermissions";
import useArchiveIndexSetConfigurationMutation from "archive/hooks/useArchiveIndexSetConfigurationMutation";
import ArchiveRoutes from "archive/Routes";
const FlexWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.md};
  `
);
const SubmitWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const validateForm = (values) => {
  const errors = {};
  if (!(values.shards > 0)) {
    errors.shards = "At minimum 1 shard is required.";
  }
  if (!(values.replicas >= 0)) {
    errors.replicas = "Cannot be negative.";
  }
  if (values.automated_shard_management_enabled && !(values.shard_size_bytes > 0)) {
    errors.shard_size_bytes = "Cannot be negative.";
  }
  const requiredFields = ["shards", "replicas"];
  requiredFields.forEach((field) => {
    if (values[field] === "" || typeof values[field] === "undefined") {
      errors[field] = "Is required.";
    }
  });
  return errors;
};
const RestoredIndexSetConfigurationForm = () => {
  const { push } = useHistory();
  const { data: indexSetData, isLoading: isLoadingIndexSet } = useArchiveIndexSetConfiguration();
  const { updateArchiveIndexSetConfigurationMutation } = useArchiveIndexSetConfigurationMutation();
  const { isPermitted } = usePermissions();
  const ignoreFieldRestrictions = isPermitted("indexsets_field_restrictions:edit");
  const initialValues = useMemo(() => {
    if (!indexSetData)
      return {
        shards: void 0,
        replicas: void 0,
        automated_shard_management_enabled: void 0,
        shard_size_bytes: void 0,
        field_restrictions: void 0
      };
    const { shards, replicas, automated_shard_management_enabled, shard_size_bytes, field_restrictions } = indexSetData;
    return {
      shards,
      replicas,
      automated_shard_management_enabled,
      shard_size_bytes,
      field_restrictions
    };
  }, [indexSetData]);
  if (isLoadingIndexSet) return /* @__PURE__ */ React.createElement(Spinner, null);
  if (!indexSetData) return null;
  const { immutableFields, hiddenFields } = parseFieldRestrictions(indexSetData.field_restrictions);
  const handleAutomatedShardManagementChange = (value, setFieldValue, setFieldTouched) => {
    setFieldValue("automated_shard_management_enabled", !value);
    if (!value === false) {
      setFieldValue("shard_size_bytes", indexSetData.shard_size_bytes);
      setFieldTouched("shard_size_bytes");
    }
  };
  const handleSubmit = (values) => {
    updateArchiveIndexSetConfigurationMutation(values).then(() => {
      push(ArchiveRoutes.INDEX_SET.SHOW);
    });
  };
  const handleCancel = () => {
    push(ArchiveRoutes.INDEX_SET.SHOW);
  };
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    Formik,
    {
      validateOnMount: true,
      onSubmit: handleSubmit,
      enableReinitialize: true,
      validate: validateForm,
      initialValues
    },
    ({ isValid, isValidating, isSubmitting, values, setFieldValue, setFieldTouched }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(FlexWrapper, null, /* @__PURE__ */ React.createElement(Section, { title: "Details" }, /* @__PURE__ */ React.createElement(HideOnCloud, null, /* @__PURE__ */ React.createElement(HiddenFieldWrapper, { hiddenFields, ignoreFieldRestrictions }, /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "shards",
        label: "Index Shards",
        name: "shards",
        help: /* @__PURE__ */ React.createElement(React.Fragment, null, "Restoring an Archive, or Retrieving from Data Lake, will create an index in this Index Set. This field sets the number of shards created per index. Increasing the Shard Count improves write speed. This value should not be set higher than the number of search nodes.", /* @__PURE__ */ React.createElement("br", null), 'Note: When "Automated Shard Management" is enabled, this value will be managed dynamically, and the value here only used as a fallback when the size of the restore or retrieval job is unknown.'),
        required: true,
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("shards")) && !ignoreFieldRestrictions
      }
    ), /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "replicas",
        label: "Index Replica",
        name: "replicas",
        help: "Number of search cluster Replica Shards used per Index in this Index Set. Adding Replica Shards improves search performance during parallel reads of the index, such as occurs on dashboards, and is a component of HA and backup strategy. Each Replica Shard set multiplies the storage requirement and memory footprint of the index. This value should not be set higher than the number of search nodes, and typically not higher than 1.                                   ",
        required: true,
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("replicas")) && !ignoreFieldRestrictions
      }
    ), /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "checkbox",
        id: "automated-shard-management-enabled",
        label: "Automated Shard Management",
        name: "automated_shard_management_enabled",
        help: 'Automatically scale the count of shards for indexes created by Archive Restore or Data Lake Retrieval jobs. The count of shards created will not exceed the count of Data Nodes. The value set will be proportional to the size of the Restore or Retrieval job, divided by the "Size per additional shard" value below.',
        onChange: () => handleAutomatedShardManagementChange(
          values.automated_shard_management_enabled,
          setFieldValue,
          setFieldTouched
        ),
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("automated_shard_management_enabled")) && !ignoreFieldRestrictions
      }
    ), values.automated_shard_management_enabled && /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "shard-size-bytes",
        label: "Size of restore/retrieval per additional shard",
        name: "shard_size_bytes",
        help: /* @__PURE__ */ React.createElement(React.Fragment, null, "When Automated shard management is enabled, the maximum count of shards in an index created for an Archive Restore or Data Lake Retrieval job will be dictated by", " ", /* @__PURE__ */ React.createElement("strong", null, "[Size of the Restore or Retrieval / Value in this field]"), ". We recommend setting a value no larger than 0.6x the RAM provision of a Opensearch or Datanode server in your cluster. Smaller values will result in parallelism being triggered by smaller jobs."),
        required: true,
        addonAfter: NumberUtils.formatBytes(values.shard_size_bytes),
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("shard_size_bytes")) && !ignoreFieldRestrictions
      }
    )))), /* @__PURE__ */ React.createElement(SubmitWrapper, null, /* @__PURE__ */ React.createElement(
      FormSubmit,
      {
        disabledSubmit: isValidating || !isValid,
        submitButtonText: "Update Configuration",
        submitLoadingText: "Updating Configuration",
        isSubmitting,
        displayCancel: true,
        onCancel: handleCancel
      }
    ))))
  )));
};
export default RestoredIndexSetConfigurationForm;
